import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import { getEnvVar } from 'helpers';
import { Card, CardContent, Grid, Typography, Button } from '@material-ui/core';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import moment from 'moment';
import FilterListIcon from '@material-ui/icons/FilterList';

import { EmailCampaignPerformanceGraph } from 'views/Reports/components';
import { StatBoxCard } from 'views/Reports/components';

import useReport from '../useReport';
import { TemplateSingleSelect } from 'components';
import DateFilterPopover from 'views/Reports/components/DateFilterPopover';
import { updateFilterTags } from 'store/helpers/tableAndToolbar/actions';
import { switchCampaignReportData } from 'store/reports/actions';
import { getAdvancedSearchPaginatedCampaigns } from 'store/campaign/actions';
import { setCampaignFilters } from 'store/campaign/actions';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(4)
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    marginTop: 0,
    marginBottom: 0
  },
  contents: {
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
    '@media (max-width: 1500px)': {
      flexWrap: 'wrap',
      alignItems: 'flex-start'
    }
  },
  selectables: {
    marginRight: theme.spacing(2)
  },
  typeSelect: {
    fontSize: '20px'
  },
  select: {
    fontSize: '14px'
  },
  formControl: {
    display: 'flex',
    flexDirection: 'row',
    paddingRight: '12px'
  },
  formControlSms: {
    paddingRight: '4px'
  },
  formControlWords: {
    paddingRight: '20px'
  },
  dateField: {
    paddingRight: '12px',
    fontSize: '20px'
  },
  errorCard: {
    margin: '10px 0px',
    padding: 10
  },
  error: {
    color: theme.palette.error.main
  },
  selectInput: {
    '& label + .MuiInput-formControl': {
      marginRight: theme.spacing(2)
    }
  },
  selectInputFont: {
    '& .MuiSelect-select': {
      fontSize: '20px'
    }
  },
  menu: {
    fontSize: '20px',
    paddingTop: '0px',
    paddingLeft: '0px',
    paddingRight: theme.spacing(2),
    margin: '0px'
  },
  campaignSelect: {
    width: 220,
    marginRight: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      height: 36,
      backgroundColor: theme.palette.common.white
    },
    '& .MuiOutlinedInput-input': {
      paddingTop: '8px',
      paddingBottom: '8px',
      fontSize: '0.875rem'
    },
    '& .MuiInputLabel-outlined': {
      transform: 'translate(14px, 10px) scale(1)'
    },
    '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)'
    }
  },
  campaignSelectSms: {
    width: 160,
    marginRight: theme.spacing(0.5)
  },
  campaignSelectPanel: {
    marginTop: theme.spacing(1),
    width: 560,
    maxWidth: '78vw',
    '& > div': {
      height: 360
    }
  },
  filterCard: {
    marginBottom: theme.spacing(2)
  },
  filterCardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2)
    }
  },
  dateTypeToggleGroup: {
    margin: 0,
    '& .MuiToggleButton-root': {
      textTransform: 'none',
      fontWeight: 600,
      padding: '4px 12px',
      minHeight: 32,
      height: 32,
      lineHeight: 1,
      boxSizing: 'border-box'
    }
  },
  outsideDateTypeToggle: {
    marginRight: theme.spacing(1)
  },
  headerRows: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    rowGap: theme.spacing(1)
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    minWidth: 0
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: theme.spacing(1),
    flexShrink: 0
  },
  topRowControls: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  dateTopControls: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0
  },
  leftControlsRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'nowrap',
    gap: theme.spacing(1)
  },
  rightControlsGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
    flexWrap: 'nowrap',
    '@media (max-width: 1200px)': {
      marginLeft: 0,
      width: '100%',
      justifyContent: 'flex-start',
      flexWrap: 'wrap'
    }
  },
  calendarButtonRow: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    maxWidth: '100%'
  },
  calendarButton: {
    minHeight: 32,
    height: 32,
    paddingTop: 4,
    paddingBottom: 4,
    boxSizing: 'border-box',
    maxWidth: '100%',
    '@media (max-width: 1200px)': {
      width: 'auto',
      justifyContent: 'flex-start'
    }
  }
}));

const Summary = props => {
  const classes = useStyles();
  const [suppressContextFetch, setSuppressContextFetch] = useState(false);
  const report = useReport({ suppressContextFetch });
  const dispatch = useDispatch();
  const {
    queryData,
    updateParams,
    applyFilters,
    error,
    getMaxAvailableDailyDate,
    getMinAvailableDailyDate,
    getMaxAvailableMonthlyDate,
    getMinAvailableMonthlyDate,
    getPresetRange,
    getMatchingPreset,
    dailyReportDelayHours,
    monthlyReportDelayHours,
    dailyRuntime,
    monthlyRuntime
  } = report;

  const configuredExpirationMonthsInDays = parseInt(
    getEnvVar('ADM_REPORT_EXPIRATION_MONTHS_IN_DAYS'),
    10
  );
  const expirationMonthsInDays = Number.isFinite(configuredExpirationMonthsInDays)
    ? Math.max(configuredExpirationMonthsInDays, 730)
    : 730;

  const currentCampaigns = useSelector(store => store.campaign.advancedPaginatedList);
  const isCampaignFilterLoading = useSelector(store => store.campaign.gettingPaginated);
  const filteredTags = useSelector(store => store.tableAndToolbar.filterTags);
  const businessContextId = useSelector(store => store.user.selectedBusinessContext?.businessContextId);
  const contextDetails = useSelector(store => store.user.contextDetails || []);
  const isAllContext = businessContextId === 'Default';

  const [allCampaigns, setAllCampaigns] = useState([]);
  const [campaignStatusFilter, setCampaignStatusFilter] = useState('all');
  const [campaignSearchByValues, setCampaignSearchByValues] = useState(['property']);
  const [campaignTypeFilter, setCampaignTypeFilter] = useState('EMAIL');
  const [smsProviderFilter, setSmsProviderFilter] = useState('VONAGE');
  const [campaignPromotionalFilter, setCampaignPromotionalFilter] = useState('all');
  const [dateFilterAnchorEl, setDateFilterAnchorEl] = useState(null);
  const [dateFilterError, setDateFilterError] = useState('');
  const [dateFilterDraft, setDateFilterDraft] = useState({
    rangePreset: 'LAST_7_DAYS',
    fromDate: '',
    toDate: ''
  });
  const [rangeSelectionStep, setRangeSelectionStep] = useState('start');
  const [visibleDailyMonth, setVisibleDailyMonth] = useState(moment().startOf('month'));
  const [visibleMonthYear, setVisibleMonthYear] = useState(moment().year());
  const isDateFilterOpen = Boolean(dateFilterAnchorEl);

  useEffect(() => {
    setAllCampaigns([...currentCampaigns]);
  }, [currentCampaigns]);

  // Set tenantOnly only when businessContextId actually changes (user switches context), not on navigation
  useEffect(() => {
    // Reset campaign status filter when switching context
    setCampaignStatusFilter('all');
    setCampaignSearchByValues(isAllContext ? ['property'] : []);
  }, [isAllContext]);

  const getCampaignSearchScopes = (searchByValues = campaignSearchByValues) => {
    if (!isAllContext) {
      return [];
    }

    if (Array.isArray(searchByValues) && searchByValues.length > 0) {
      if (searchByValues.includes('property')) {
        return null;
      }
      if (searchByValues.includes('tenantOnly')) {
        return ['tenantOnly'];
      }
    }
    // When Search By is cleared, do not pass any scope.
    return null;
  };

  useEffect(() => {
    onFilterChange('');
  }, []);

  // Reset campaign status filter when filteredTags is cleared (e.g., navigating back from details page)
  // Track previous filteredTags to detect when it changes from having values to being empty
  const prevFilteredTagsRef = React.useRef(filteredTags);
  useEffect(() => {
    const prevTags = prevFilteredTagsRef.current;
    const hadTags = prevTags && prevTags.length > 0;
    const nowEmpty = !filteredTags || filteredTags.length === 0;
    
    // Only trigger API call if tags were cleared (had values before, now empty)
    if (hadTags && nowEmpty) {
      setCampaignStatusFilter('all');
      setCampaignSearchByValues(['property']);
    }
    
    prevFilteredTagsRef.current = filteredTags;
  }, [filteredTags, businessContextId, dispatch]);

  const isCampaignExpired = endDate => {
    let date = new Date();
    date.setDate(date.getDate() - expirationMonthsInDays);
    return new Date(endDate) < date;
  };

  const isFutureCampaign = startDate => new Date(startDate) >= new Date();

  const invalidDate = (startDate, endDate) => {
    return isCampaignExpired(endDate) || isFutureCampaign(startDate);
  };

  const contextLookup = React.useMemo(() => {
    const lookup = {};
    (contextDetails || []).forEach(context => {
      lookup[context.businessContextId] = context.name;
    });
    lookup.Default = lookup.Default || 'ALL';
    return lookup;
  }, [contextDetails]);

  const selectedCampaignValue = React.useMemo(() => {
    if (Array.isArray(filteredTags)) {
      return filteredTags[0] || '';
    }
    return filteredTags || '';
  }, [filteredTags]);

  const shouldShowCampaignContext = React.useMemo(
    () => isAllContext && Array.isArray(campaignSearchByValues) && campaignSearchByValues.includes('tenantOnly'),
    [campaignSearchByValues, isAllContext]
  );

  const campaignDropdownOptions = React.useMemo(
    () =>
      (allCampaigns || [])
        .slice()
        .filter(campaign => {
          if (campaign.type !== campaignTypeFilter) return false;
          if (campaignPromotionalFilter === 'promotional' && !campaign.promotional) return false;
          if (campaignPromotionalFilter === 'nonPromotional' && campaign.promotional) return false;
          return true;
        })
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(campaign => {
          const contextName = contextLookup[campaign.businessContextId] || campaign.businessContextId;
          const futureCampaign = isFutureCampaign(campaign.startDate);
          const expiredCampaign = isCampaignExpired(campaign.endDate);
          const isArchivedCampaign = campaign.active === false;
          const showArchivedExpirationTooltip = isArchivedCampaign && expiredCampaign;
          const tooltipLines = [campaign.name];

          if (futureCampaign) {
            tooltipLines.push(`${campaign.name} has not started yet`);
          } else if (showArchivedExpirationTooltip) {
            tooltipLines.push(`ended more than ${expirationMonthsInDays} days ago`);
          }

          return {
            uuid: campaign.uuid,
            name: campaign.name,
            type: campaign.type,
            disabled: invalidDate(campaign.startDate, campaign.endDate),
            toolTip: tooltipLines.join('\n'),
            secondaryText: shouldShowCampaignContext ? `Context: ${contextName}` : '',
            searchText: shouldShowCampaignContext ? `${campaign.name} ${contextName}` : campaign.name
          };
        }),
    [allCampaigns, contextLookup, expirationMonthsInDays, shouldShowCampaignContext, campaignTypeFilter, campaignPromotionalFilter]
  );

  const onFilterChange = selectedCampaign => {
    const value = selectedCampaign?.value || '';
    dispatch(updateFilterTags(value));
    dispatch(
      switchCampaignReportData({
        value,
        type: selectedCampaign?.type,
        openFilter: false
      })
    );
  };

  const handleCampaignSelectionChange = event => {
    const selectedValue = event?.target?.value || '';
    const selectedCampaign = campaignDropdownOptions.find(
      option => option.uuid === selectedValue
    );
    setSuppressContextFetch(Boolean(selectedCampaign?.uuid));

    onFilterChange(
      selectedCampaign
        ? { value: selectedCampaign.uuid, type: selectedCampaign.type }
        : { value: '' }
    );
  };

  const handleCampaignStatusChange = (newStatus) => {
    setCampaignStatusFilter(newStatus);
    // Server-side filtering: convert status to active filter value
    let activeFilter = null;
    if (newStatus === 'active') {
      activeFilter = true;
    } else if (newStatus === 'archived') {
      activeFilter = false;
    }
    dispatch(getAdvancedSearchPaginatedCampaigns({ 
      force: true, 
      activeFilter,
      type: [campaignTypeFilter],
      searchScopes: getCampaignSearchScopes(),
      promotionalFilter: campaignPromotionalFilter === 'promotional' ? true : campaignPromotionalFilter === 'nonPromotional' ? false : null
    }));
  };

  const handleCampaignSearch = (searchTerm) => {
    // Server-side search: convert status to active filter value
    let activeFilter = null;
    if (campaignStatusFilter === 'active') {
      activeFilter = true;
    } else if (campaignStatusFilter === 'archived') {
      activeFilter = false;
    }
    dispatch(getAdvancedSearchPaginatedCampaigns({ 
      force: true, 
      activeFilter,
      type: [campaignTypeFilter],
      searchScopes: getCampaignSearchScopes(),
      searchName: searchTerm,
      promotionalFilter: campaignPromotionalFilter === 'promotional' ? true : campaignPromotionalFilter === 'nonPromotional' ? false : null
    }));
  };

  const handleCampaignSearchByChange = (newValues) => {
    setCampaignSearchByValues(newValues);
    let activeFilter = null;
    if (campaignStatusFilter === 'active') {
      activeFilter = true;
    } else if (campaignStatusFilter === 'archived') {
      activeFilter = false;
    }
    dispatch(getAdvancedSearchPaginatedCampaigns({
      force: true,
      activeFilter,
      type: [campaignTypeFilter],
      searchScopes: getCampaignSearchScopes(newValues),
      promotionalFilter: campaignPromotionalFilter === 'promotional' ? true : campaignPromotionalFilter === 'nonPromotional' ? false : null
    }));
  };

  const handleCampaignDropdownOpen = () => {
    let activeFilter = null;
    if (campaignStatusFilter === 'active') {
      activeFilter = true;
    } else if (campaignStatusFilter === 'archived') {
      activeFilter = false;
    }

    dispatch(getAdvancedSearchPaginatedCampaigns({
      force: true,
      activeFilter,
      type: [campaignTypeFilter],
      searchScopes: getCampaignSearchScopes(),
      promotionalFilter: campaignPromotionalFilter === 'promotional' ? true : campaignPromotionalFilter === 'nonPromotional' ? false : null
    }));
  };

  // Clear selected campaign when campaign type filter changes
  useEffect(() => {
    if (selectedCampaignValue) {
      const selectedCampaign = (allCampaigns || []).find(c => c.uuid === selectedCampaignValue);
      if (selectedCampaign && selectedCampaign.type !== campaignTypeFilter) {
        onFilterChange({ value: '' });
      }
    }
  }, [campaignTypeFilter, selectedCampaignValue, allCampaigns]);

  useEffect(() => {
    if (queryData.type && queryData.type !== campaignTypeFilter) {
      setCampaignTypeFilter(queryData.type);
    }
  }, [queryData.type, campaignTypeFilter]);

  useEffect(() => {
    if (queryData.providerType && queryData.providerType !== smsProviderFilter) {
      setSmsProviderFilter(queryData.providerType);
    }
  }, [queryData.providerType, smsProviderFilter]);

  const campaignStatusOptions = [
    { value: 'all', label: 'All' },
    { value: 'active', label: 'Active' },
    { value: 'archived', label: 'Archived' }
  ];

  const campaignSearchByOptions = [
    { value: 'tenantOnly', label: 'Tenant' },
    { value: 'property', label: 'Property' }
  ];

  const smsProviderOptions = [
    { value: 'VONAGE', label: 'Vonage' },
    { value: 'TWILIO', label: 'Twilio' }
  ];

  const handleCampaignTypeChange = (newType) => {
    if (!newType || newType === campaignTypeFilter) {
      return;
    }
    setCampaignTypeFilter(newType);
    dispatch(setCampaignFilters({ filterTypes: [newType] }));
    // Update the summary type display
    updateParams('type', newType);
  };

  const handleSmsProviderChange = (newProvider) => {
    if (!newProvider || newProvider === smsProviderFilter) {
      return;
    }
    setSmsProviderFilter(newProvider);
    // Update the provider type display
    updateParams('providerType', newProvider);
  };

  const promotionalFilterOptions = [
    { value: 'all', label: 'All' },
    { value: 'promotional', label: 'Promotional' },
    { value: 'nonPromotional', label: 'Non-Promotional' }
  ];

  const handlePromotionalFilterChange = (newValue) => {
    setCampaignPromotionalFilter(newValue);
    let activeFilter = null;
    if (campaignStatusFilter === 'active') {
      activeFilter = true;
    } else if (campaignStatusFilter === 'archived') {
      activeFilter = false;
    }
    const promotionalFilter = newValue === 'promotional' ? true : newValue === 'nonPromotional' ? false : null;
    dispatch(getAdvancedSearchPaginatedCampaigns({
      force: true,
      activeFilter,
      type: [campaignTypeFilter],
      searchScopes: getCampaignSearchScopes(),
      promotionalFilter
    }));
  };

  const openDateFilter = event => {
    // Try to find which preset matches the current date range
    const matchingPreset = getMatchingPreset(queryData.fromDate, queryData.toDate, queryData.dateType);
    
    const nextDraft = {
      rangePreset: matchingPreset || (queryData.dateType === 'MONTH' ? 'LAST_24_MONTHS' : 'LAST_60_DAYS'),
      fromDate: queryData.fromDate || '',
      toDate: queryData.toDate || ''
    };
    setDateFilterError('');
    setDateFilterDraft(nextDraft);
    setRangeSelectionStep('start');
    setVisibleDailyMonth(
      moment(nextDraft.toDate || nextDraft.fromDate || getMaxAvailableDailyDate()).startOf('month')
    );
    setVisibleMonthYear(moment(nextDraft.toDate || nextDraft.fromDate || getMaxAvailableMonthlyDate()).year());
    setDateFilterAnchorEl(event.currentTarget);
  };

  const closeDateFilter = () => {
    setDateFilterError('');
    setDateFilterAnchorEl(null);
  };

  const applyDateFilters = () => {
    const fromDate = moment(dateFilterDraft.fromDate);
    const toDate = moment(dateFilterDraft.toDate);
    const effectiveDateType = queryData.dateType || 'DAY';

    if (!dateFilterDraft.fromDate || !dateFilterDraft.toDate) {
      setDateFilterError('Please select both start and end dates');
      return;
    }

    if (fromDate.isAfter(toDate)) {
      setDateFilterError('Start date cannot be after end date');
      return;
    }

    const nextQueryData = { ...queryData, dateType: effectiveDateType };

    if (effectiveDateType === 'DAY') {
      nextQueryData.fromDate = fromDate.format('YYYY-MM-DD');
      nextQueryData.toDate = toDate.format('YYYY-MM-DD');
    }

    if (effectiveDateType === 'MONTH') {
      const fromMonth = moment(dateFilterDraft.fromDate).startOf('month');
      const toMonth = moment(dateFilterDraft.toDate).endOf('month');
      const [minMonth, maxMonth] = fromMonth.isBefore(toMonth)
        ? [fromMonth, toMonth]
        : [toMonth, fromMonth];
      nextQueryData.fromDate = minMonth.format('YYYY-MM-DD');
      nextQueryData.toDate = maxMonth.format('YYYY-MM-DD');
    }

    applyFilters({
      dateType: nextQueryData.dateType,
      fromDate: nextQueryData.fromDate,
      toDate: nextQueryData.toDate
    });
    closeDateFilter();
  };

  const handleDateTypeChange = (event, value) => {
    if (!value || value === queryData.dateType) return;

    if (isDateFilterOpen) {
      const defaultPreset = value === 'MONTH' ? 'LAST_24_MONTHS' : 'LAST_60_DAYS';
      const range = getPresetRange(defaultPreset, value);
      setDateFilterDraft({
        rangePreset: defaultPreset,
        fromDate: range.fromDate,
        toDate: range.toDate
      });
      setRangeSelectionStep('start');
      setVisibleMonthYear(moment(range.toDate).year());
      if (value === 'DAY') {
        setVisibleDailyMonth(moment(range.toDate).startOf('month'));
      }
    }
    updateParams('dateType', value);
  };

  const getDateFilterButtonLabel = () => {
    if (queryData.dateType === 'MONTH') {
      const fromMonth = queryData.fromDate ? moment(queryData.fromDate).format('MM-YYYY') : '--';
      const toMonth = queryData.toDate ? moment(queryData.toDate).format('MM-YYYY') : '--';
      return `monthly (${fromMonth}-${toMonth})`;
    }
    const fromDate = queryData.fromDate ? moment(queryData.fromDate).format('MM/DD/YYYY') : '--';
    const toDate = queryData.toDate ? moment(queryData.toDate).format('MM/DD/YYYY') : '--';
    return `daily (${fromDate}-${toDate})`;
  };

  return (
    <div className={classes.root}>
      <Card className={classes.filterCard}>
        <CardContent className={classes.filterCardContent}>
          <div className={classes.headerRows}>
            <div className={classes.leftControlsRow}>
              <TemplateSingleSelect
                label="Campaigns"
                name="campaignId"
                value={selectedCampaignValue}
                onChange={handleCampaignSelectionChange}
                options={{ list: campaignDropdownOptions, valueId: 'uuid' }}
                fullWidth={false}
                margin="none"
                dataTestId="campaign-reports-campaign-filter"
                className={classes.campaignSelect}
                panelClassName={classes.campaignSelectPanel}
                textFieldProps={{ size: 'small' }}
                popperPlacement="bottom-start"
                isLoading={isCampaignFilterLoading}
                statusOptions={campaignStatusOptions}
                selectedStatusValue={campaignStatusFilter}
                onStatusChange={handleCampaignStatusChange}
                searchByOptions={isAllContext ? campaignSearchByOptions : undefined}
                selectedSearchByValues={isAllContext ? campaignSearchByValues : undefined}
                onSearchByChange={isAllContext ? handleCampaignSearchByChange : undefined}
                promotionalFilterOptions={promotionalFilterOptions}
                selectedPromotionalValue={campaignPromotionalFilter}
                onPromotionalFilterChange={handlePromotionalFilterChange}
                onOpen={handleCampaignDropdownOpen}
                onSearchChange={handleCampaignSearch}
              />
            </div>

            <div className={classes.topRowControls}>
              <ToggleButtonGroup
                value={campaignTypeFilter}
                exclusive
                className={`${classes.dateTypeToggleGroup} ${classes.outsideDateTypeToggle}`}
                onChange={(event, value) => handleCampaignTypeChange(value)}>
                <ToggleButton value="EMAIL">Email</ToggleButton>
                <ToggleButton value="SMS">SMS</ToggleButton>
              </ToggleButtonGroup>

              {campaignTypeFilter === 'SMS' && (
                <ToggleButtonGroup
                  value={smsProviderFilter}
                  exclusive
                  className={`${classes.dateTypeToggleGroup} ${classes.outsideDateTypeToggle}`}
                  onChange={(event, value) => handleSmsProviderChange(value)}>
                  {smsProviderOptions.map(option => (
                    <ToggleButton key={option.value} value={option.value}>{option.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
              )}
            </div>

            <div className={classes.rightControlsGroup}>
              <div className={classes.dateTopControls}>
                <ToggleButtonGroup
                  value={queryData.dateType || 'DAY'}
                  exclusive
                  className={classes.dateTypeToggleGroup}
                  onChange={handleDateTypeChange}>
                  <ToggleButton value="DAY">Daily</ToggleButton>
                  <ToggleButton value="MONTH">Monthly</ToggleButton>
                </ToggleButtonGroup>
              </div>

              <div className={classes.calendarButtonRow}>
                <Button
                  className={classes.calendarButton}
                  color="primary"
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={openDateFilter}>
                  {getDateFilterButtonLabel()}
                </Button>
              </div>
            </div>
          </div>

          <DateFilterPopover
            dateFilterDraft={dateFilterDraft}
            rangeSelectionStep={rangeSelectionStep}
            visibleDailyMonth={visibleDailyMonth}
            visibleMonthYear={visibleMonthYear}
            minAvailableDailyDate={getMinAvailableDailyDate()}
            maxAvailableDailyDate={getMaxAvailableDailyDate()}
            minAvailableMonthlyDate={getMinAvailableMonthlyDate()}
            maxAvailableMonthlyDate={getMaxAvailableMonthlyDate()}
            onDateFilterChange={setDateFilterDraft}
            onRangeSelectionStepChange={setRangeSelectionStep}
            onVisibleDailyMonthChange={setVisibleDailyMonth}
            onVisibleMonthYearChange={setVisibleMonthYear}
            onApplyDateFilters={applyDateFilters}
            onCloseDateFilter={closeDateFilter}
            dateFilterError={dateFilterError}
            dateType={queryData.dateType || 'DAY'}
            anchorEl={dateFilterAnchorEl}
            getPresetRange={getPresetRange}
            queryData={queryData}
            dailyReportDelayHours={dailyReportDelayHours}
            monthlyReportDelayHours={monthlyReportDelayHours}
            dailyRuntime={dailyRuntime}
            monthlyRuntime={monthlyRuntime}
          />
        </CardContent>
      </Card>
      {error && error?.message && (
        <Card className={classes.errorCard}>
          <Typography variant="subtitle2" className={classes.error}>
            {error && error?.message}
          </Typography>
        </Card>
      )}
      {queryData.type === 'EMAIL' && (
        <>
          <StatBoxCard report={report} hideHeaderTitle />
          <EmailCampaignPerformanceGraph report={report} />
        </>
      )}
      {queryData.type === 'SMS' && (
        <div style={{ marginTop: 20 }}>
          <StatBoxCard report={report} hideHeaderTitle />
        </div>
      )}
    </div>
  );
};

export default Summary;
